'use client'

import { consentedTo, ConsentGroup } from '@repo/cookie/consent'
import { imageQuality, urlForImage } from '@repo/sanity/image'
import { NewsletterQueryResult } from '@repo/sanity/types'
import { hasInStorage, LOCAL_STORAGE_EMAIL_UNVERIFIED, LOCAL_STORAGE_NEWSLETTER_DIALOG, setInStorage } from '@repo/storage/service'
import { Button } from '@repo/ui/components/button'
import { Dialog, DialogAside, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/components/dialog'
import { Input } from '@repo/ui/components/input'
import { cn } from '@repo/ui/lib/utils'
import { Image } from 'next-sanity/image'
import { usePathname } from 'next/navigation'
import { FunctionComponent, useActionState, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { subscribe } from '../../actions/newsletter.actions'
import { DIALOG, useDialog } from '../../providers/DialogProvider'
import { useSheet } from '../../providers/SheetProvider'
import { useTelemetry } from '../../providers/TelemetryProvider'
import { PageDto } from '../../types/page.types'

interface Props {
  pageDto: PageDto
  newsletter: NewsletterQueryResult
}

const NewsletterDialog: FunctionComponent<Props> = ({ pageDto, newsletter }) => {
  const { openSheetId } = useSheet()
  const pathname = usePathname()
  const { trackElevarSubscribeEvent } = useTelemetry()
  const [isExpanded, setIsExpanded] = useState(false)
  const { openDialogIds, openDialog, isDialogOpen, closeDialog } = useDialog()
  const toastShownRef = useRef<{ [key: string]: boolean }>({})
  const [state, formAction, pending] = useActionState(subscribe, null)
  const imageUrl = newsletter?.dialogImage && urlForImage(newsletter?.dialogImage)?.width(112)?.height(168)?.url()
  const denyList = useMemo(() => ['/product', ...(newsletter?.urlPathDenyList || [])], [newsletter?.urlPathDenyList])
  const localeStorageKey: Readonly<string> = `${LOCAL_STORAGE_NEWSLETTER_DIALOG}-${newsletter?._id || ''}`
  const persist = newsletter?.dialogPersists || false

  const suppressNewsletter = useCallback((): boolean => {
    return denyList.some((denyItem) => pathname.includes(denyItem))
  }, [denyList, pathname])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setInStorage(localeStorageKey, Date.now())

      if (!open) {
        closeDialog(DIALOG.NEWSLETTER)
      }
    },
    [closeDialog, localeStorageKey],
  )

  const handleSubmit = () => {
    toastShownRef.current = {}
  }

  // On load.
  useEffect(() => {
    if (openDialogIds.size === 0 && openSheetId === null && !suppressNewsletter() && (persist || (!persist && !hasInStorage(localeStorageKey)))) {
      openDialog(DIALOG.NEWSLETTER)
    }
  }, [localeStorageKey, openDialog, openDialogIds, openSheetId, persist, suppressNewsletter])

  // On success.
  useEffect(() => {
    if (pending || !state?.status) {
      return
    }

    if (!toastShownRef.current[state.status]) {
      if (state.status === 'success') {
        toast.success(newsletter?.dialogSuccessMessage || 'Thank you for subscribing!')
      } else if (state.status === 'error') {
        toast.error(state?.message || 'Something went wrong.')
      }

      // Mark the toast as shown for this status, as
      // to prevent any duplicate toasts due to re-renders.
      toastShownRef.current[state?.status] = true
    }

    if (!pending && state?.status === 'success') {
      if (!persist) {
        handleOpenChange(false)
      }

      if (state?.validatedFields?.data?.email) {
        if (consentedTo(ConsentGroup.PREFERENCES)) {
          setInStorage(LOCAL_STORAGE_EMAIL_UNVERIFIED, state.validatedFields.data.email)
        }

        trackElevarSubscribeEvent({
          lead_type: 'email',
          user_properties: {
            customer_email: state.validatedFields.data.email,
          },
        })
      }
    }
  }, [handleOpenChange, newsletter?.dialogSuccessMessage, pending, persist, state, trackElevarSubscribeEvent])

  if (!newsletter?.dialogTitle || !newsletter?.dialogCopy || !newsletter?.dialogKlaviyoList) {
    return null
  }

  const FieldError: FunctionComponent<{ field: string }> = ({ field }) => {
    return <p className={cn('text-destructive pt-2 empty:hidden', pending && 'hidden')}>{state?.status === 'error' ? (state?.errors as any)?.[field]?.[0] : null}</p>
  }

  return (
    <Dialog open={isDialogOpen(DIALOG.NEWSLETTER)} onOpenChange={handleOpenChange} modal={false}>
      <DialogContent
        className='flex-col gap-3 sm:max-w-[24rem]'
        position={newsletter?.dialogPosition}
        background={newsletter?.dialogBackground}
        close={persist ? 'none' : newsletter?.dialogClose}
      >
        <div className='flex items-stretch justify-between gap-3'>
          {imageUrl && (
            <DialogAside className='relative aspect-[2/3]'>
              <Image
                className='rounded-lg object-cover'
                fill={true}
                priority={true}
                quality={imageQuality}
                alt={newsletter?.dialogImage?.alt || newsletter?.dialogTitle || ''}
                src={imageUrl}
                sizes='7rem'
              />
            </DialogAside>
          )}
          <div className='flex flex-col justify-between'>
            <DialogHeader className={cn(newsletter.dialogTitleFont === 'mono' && 'font-mono', newsletter.dialogTitleSize === 'large' && 'text-lg')}>
              <DialogTitle>{newsletter?.dialogTitle}</DialogTitle>
            </DialogHeader>
            <DialogFooter className='justify-between'>
              <DialogDescription className={cn('w-10/12 text-pretty', newsletter.dialogTitleFont === 'mono' && 'font-mono', newsletter.dialogExpandedByDefault && 'pt-3')}>
                {newsletter?.dialogCopy || 'Sign up to our newsletter'}
              </DialogDescription>
              {!newsletter.dialogExpandedByDefault && (
                <Button variant='defaultStatic' onClick={() => setIsExpanded((prev) => !prev)} className={cn(isExpanded && 'invisible opacity-0')}>
                  {newsletter?.dialogButtonLabel || 'Sign Up'}
                </Button>
              )}
            </DialogFooter>
          </div>
        </div>
        <form
          className={cn('flex-1 basis-full flex-col gap-3 pb-2', isExpanded || newsletter.dialogExpandedByDefault ? 'flex' : 'hidden')}
          action={formAction}
          onSubmit={handleSubmit}
        >
          <input type='hidden' name='locale' value={pageDto.params.locale} />
          <input type='hidden' name='klaviyoList' value={newsletter?.dialogKlaviyoList} />
          <div className='flex justify-between gap-3'>
            <div className='w-1/2'>
              <Input type='text' name='firstName' variant='underlined' placeholder={pageDto.dictionary?.newsletter?.firstname || 'First Name'} autoComplete='given-name' />
              <FieldError field='firstName' />
            </div>
            <div className='w-1/2'>
              <Input type='text' name='lastName' variant='underlined' placeholder={pageDto.dictionary?.newsletter?.lastname || 'Last Name'} autoComplete='family-name' />
              <FieldError field='lastName' />
            </div>
          </div>
          <div>
            <Input type='email' name='email' variant='underlined' placeholder={pageDto.dictionary?.newsletter?.email || 'Email'} autoComplete='email' required={true} />
            <FieldError field='email' />
          </div>
          <Button className='flex' disabled={pending} variant={state?.status === 'error' && !pending ? 'destructive' : 'defaultStatic'} size='lg'>
            {newsletter?.dialogButtonLabel || 'Sign Up'}
          </Button>
          {newsletter?.dialogDisclaimer && <p className='text-muted'>{newsletter?.dialogDisclaimer}</p>}
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NewsletterDialog
